import React from 'react';
import { ProjectTag, ProjectTagType } from '@/types';
import { useGetProjectId } from '@/utils/hooks';
import { useRooms } from '@/services/rooms';
import { getProjectTags } from '@/services/project-tags';
import { useQuery } from '@tanstack/react-query';
import Tags, { TagsProps } from '@/components/Tags/Tags';

export interface TagsDisplayProps extends Omit<TagsProps, 'tags'> {
    tagIds: number[];
}

const TagsDisplay: React.FC<TagsDisplayProps> = ({ tagIds, ...tagsProps }) => {
    const projectId = useGetProjectId();
    const roomsQuery = useRooms(projectId!);
    
    const projectTagsQuery = useQuery({
        queryKey: ['project-tags', projectId, ProjectTagType.Room],
        queryFn: () => getProjectTags(projectId!, ProjectTagType.Room),
        enabled: !!projectId,
    });

    const allTags = React.useMemo(() => {
        const projectTags = projectTagsQuery.data || [];
        const rooms = roomsQuery.data || [];
        
        const roomTags: ProjectTag[] = rooms.map(room => ({
            id: room.id,
            label: room.name,
            type: ProjectTagType.NewRoom,
            sortOrder: room.sortOrder,
        }));
        
        const allProjectTags = [...projectTags, ...roomTags];
        
        return allProjectTags.filter(tag => tagIds.includes(tag.id));
    }, [projectTagsQuery.data, roomsQuery.data, tagIds]);

    if (projectTagsQuery.isLoading || roomsQuery.isLoading) {
        return null;
    }

    return <Tags {...tagsProps} tags={allTags} />;
};

export default TagsDisplay;